import { bmi, curWeight } from "./energy";
import { parseDay } from "./date";
import type { KayaState } from "./types";

/**
 * Figures derived from what the profile already collects — height, weight, waist, sex, year of
 * birth — plus the measured activity Kaya records anyway.
 *
 * Every one of these is a population formula, not a measurement. They are useful for watching a
 * direction of travel over weeks; they are not a body scan, and none of them should be read as a
 * diagnosis. Where a figure is unusually weak — BMI for anyone carrying muscle — the UI says so
 * rather than leaving the number to speak for itself.
 */

export type BmiBand = "under" | "healthy" | "over" | "obese";

export function bmiBand(value: number): BmiBand {
  if (value < 18.5) return "under";
  if (value < 25) return "healthy";
  if (value < 30) return "over";
  return "obese";
}

export const BMI_BAND_LABEL: Record<BmiBand, string> = {
  under: "under the healthy range",
  healthy: "in the healthy range",
  over: "above the healthy range",
  obese: "well above the healthy range",
};

/**
 * The WHO's Asian action points (Lancet 2004): risk rises from a BMI of 23, and 27.5 is where the
 * international bands' "obese" risk begins. Shown beside the WHO bands, never instead of them,
 * because the owner is in India and the international cut-offs understate her risk (ADR-033 §7).
 */
export function bmiBandAsian(value: number): BmiBand {
  if (value < 18.5) return "under";
  if (value < 23) return "healthy";
  if (value < 27.5) return "over";
  return "obese";
}

export const BMI_BAND_NAME: Record<BmiBand, string> = {
  under: "underweight",
  healthy: "healthy",
  over: "overweight",
  obese: "obese",
};

export const BMI_RANGE_WHO: Record<BmiBand, string> = {
  under: "under 18.5",
  healthy: "18.5–24.9",
  over: "25–29.9",
  obese: "30+",
};

export const BMI_RANGE_ASIAN: Record<BmiBand, string> = {
  under: "under 18.5",
  healthy: "18.5–22.9",
  over: "23–27.4",
  obese: "27.5+",
};

/** "Overweight on WHO bands (25–29.9) · Asian bands: 23–27.4 overweight, 27.5+ obese" — a band
 *  in words, never a label on its own (research B3). */
export function bmiBandsLine(value: number): string {
  const who = bmiBand(value);
  const asian = bmiBandAsian(value);
  const name = BMI_BAND_NAME[who];
  const tail = asian === "over" ? `, ${BMI_RANGE_ASIAN.obese} ${BMI_BAND_NAME.obese}` : "";
  return (
    `${name[0].toUpperCase()}${name.slice(1)} on WHO bands (${BMI_RANGE_WHO[who]}) · ` +
    `Asian bands: ${BMI_RANGE_ASIAN[asian]} ${BMI_BAND_NAME[asian]}${tail}`
  );
}

/** The weight span that is 18.5–22.9 on the Asian bands at this height. */
export function healthyWeightRangeAsian(S: KayaState): { low: number; high: number } {
  const h = S.profile.heightCm / 100;
  return { low: 18.5 * h * h, high: 22.9 * h * h };
}

/** The waist at which waist-to-height reaches 0.50 — "under 86 cm" for 173 cm. */
export function whtrAimCm(S: KayaState): number | null {
  return S.profile.heightCm ? 0.5 * S.profile.heightCm : null;
}

/** Deurenberg's standard error is about 4 points of body fat; the range shows it (research B4). */
export const BODY_FAT_SEE = 4;

/**
 * Body fat from BMI, age and sex (Deurenberg 1991), as a range — never a point (ADR-033 §7).
 *
 * Used for the headline over RFM because it needs no waist, so it is there from the first
 * weigh-in; it may under-read for South Asian women, who carry more fat at a given BMI, which is
 * one more reason to trend it rather than trust its level.
 */
export function bodyFatRange(
  S: KayaState,
  weight: number,
  today: string
): { low: number; mid: number; high: number } | null {
  const a = age(S, parseDay(today));
  const sex = S.profile.sex;
  if (a === null || !weight || !S.profile.heightCm || (sex !== "male" && sex !== "female")) return null;
  const mid = 1.2 * bmi(S, weight) + 0.23 * a - 10.8 * (sex === "male" ? 1 : 0) - 5.4;
  if (!(mid > 3 && mid < 70)) return null;
  return { low: mid - BODY_FAT_SEE, mid, high: mid + BODY_FAT_SEE };
}

/** What is left when the fat estimate is taken away — "~51 kg est.". */
export function fatFreeMass(weight: number, fatPct: number | null): number | null {
  return fatPct === null || !weight ? null : weight * (1 - fatPct / 100);
}

/** The weight span that corresponds to a BMI of 18.5–24.9 at this height. */
export function healthyWeightRange(S: KayaState): { low: number; high: number } {
  const h = S.profile.heightCm / 100;
  return { low: 18.5 * h * h, high: 24.9 * h * h };
}

/**
 * Waist ÷ height. Arguably the more useful of the two for health risk, because it reflects where
 * the weight sits rather than only how much there is — and unlike BMI it needs no assumption about
 * body composition. The widely used guideline is to keep it under 0.5.
 */
export function waistToHeight(S: KayaState, waistCm: number): number | null {
  if (!waistCm || !S.profile.heightCm) return null;
  return waistCm / S.profile.heightCm;
}

export function whtrBand(r: number): "healthy" | "raised" | "high" {
  if (r < 0.5) return "healthy";
  if (r < 0.6) return "raised";
  return "high";
}

/**
 * Relative Fat Mass — a body-fat estimate from height and waist alone.
 *
 * Chosen over the Navy formula because that one needs a neck measurement Kaya does not ask for,
 * and over BMI-based estimates because those inherit BMI's blindness to composition. Still an
 * estimate: it assumes a typical distribution and cannot see muscle directly.
 */
export function relativeFatMass(S: KayaState, waistCm: number): number | null {
  const sex = S.profile.sex;
  if (!waistCm || !S.profile.heightCm || (sex !== "male" && sex !== "female")) return null;
  // The published constant is 64 for men and 76 for women — the term is ADDED for women, not
  // subtracted. Getting that backwards produced an estimate 24 points low, which a test caught.
  const constant = sex === "female" ? 76 : 64;
  const rfm = constant - 20 * (S.profile.heightCm / waistCm);
  return rfm > 3 && rfm < 70 ? rfm : null;
}

export function age(S: KayaState, today = new Date()): number | null {
  const y = S.profile.birthYear;
  if (!y || y < 1900 || y > today.getFullYear() - 5) return null;
  return today.getFullYear() - y;
}

/**
 * Mifflin-St Jeor resting burn — what the body spends doing nothing at all.
 *
 * This is the number missing from the calorie ring, which only ever counted deliberate work.
 */
export function bmr(S: KayaState, weightKg: number, today = new Date()): number | null {
  const a = age(S, today);
  const sex = S.profile.sex;
  if (a === null || (sex !== "male" && sex !== "female")) return null;
  const base = 10 * weightKg + 6.25 * S.profile.heightCm - 5 * a;
  return Math.round(sex === "female" ? base - 161 : base + 5);
}

/**
 * Daily burn: resting, plus the everyday living Kaya never sees, plus the work it measured.
 *
 * The usual approach multiplies BMR by a guessed "activity factor". Kaya can do better for the
 * deliberate part because it measured it, so only the incidental part is assumed — a flat 1.2,
 * the standard sedentary baseline.
 */
export function dailyBurn(
  S: KayaState,
  weightKg: number,
  measuredActiveKcal: number,
  today = new Date()
): { rest: number; baseline: number; active: number; total: number } | null {
  const rest = bmr(S, weightKg, today);
  if (rest === null) return null;
  const baseline = Math.round(rest * 0.2);
  return {
    rest,
    baseline,
    active: measuredActiveKcal,
    total: rest + baseline + measuredActiveKcal,
  };
}

export interface BodyNumbers {
  weight: number;
  bmi: number;
  band: BmiBand;
  healthy: { low: number; high: number };
  waist: number | null;
  whtr: number | null;
  fatPct: number | null;
  leanKg: number | null;
  burn: ReturnType<typeof dailyBurn>;
}

export function bodyNumbers(
  S: KayaState,
  latestWaist: number | null,
  measuredActiveKcal: number,
  today = new Date()
): BodyNumbers {
  const weight = curWeight(S);
  const value = bmi(S, weight);
  const whtr = latestWaist ? waistToHeight(S, latestWaist) : null;
  const fatPct = latestWaist ? relativeFatMass(S, latestWaist) : null;
  return {
    weight,
    bmi: value,
    band: bmiBand(value),
    healthy: healthyWeightRange(S),
    waist: latestWaist,
    whtr,
    fatPct,
    leanKg: fatPct === null ? null : weight * (1 - fatPct / 100),
    burn: dailyBurn(S, weight, measuredActiveKcal, today),
  };
}
